import React, { useEffect, useMemo, useRef, useState } from "react";
import USStatesMap, { US_PORTS, ZONE_BY_STATE, ZONE_PRICES, WAREHOUSE_LABEL } from "./USStatesMap.jsx";
import EuPortsMap, { EU_PORTS } from "./EuPortsMap.jsx";
import LeadModal from "./LeadModal.jsx";

/**
 * LogisticsTabs — 3 browser-style tabs for the delivery scenarios.
 *
 *   1. Auction → US port           (interactive US map)
 *   2. Auction → Our base          (US zone map with arrows)
 *   3. From US → Europe            (EU ports map)
 *
 * Layout per tab:
 *   ┌────────────────────────────────────────────┐
 *   │  ╱tab1╲ ╱tab2╲ ╱tab3╲                      │
 *   ├────────────────┬───────────────────────────┤
 *   │ shipping card  │            map            │
 *   │ (FROM → TO,    │                           │
 *   │  estimate, CTA)│                           │
 *   ├────────────────┴───────────────────────────┤
 *   │ legend / chips bar (zones · ports · EU)    │
 *   └────────────────────────────────────────────┘
 */
export default function LogisticsTabs(props) {
  const {
    landTable,
    bidcars,
    tabMode,
    onTabModeChange,
    originKey,
    onOriginChange,
    portKey,
    onPortChange,
    seaPortKey,
    onSeaPortChange,
    euCountry,
    onEuCountryChange,
    fromMode,
    onFromModeChange,
    landPrice,
    warehousePrice,
    seaPrice,
    euLastMilePrice,
  } = props;

  const tab = tabMode || "port";
  const setTab = (t) => onTabModeChange?.(t);

  // Lead modal state
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadCtx, setLeadCtx] = useState(null);

  // Canonical, up-to-date US port list (Oakland / Vancouver dropped). Filter the
  // backend table down to these keys so every selector stays in sync with the map.
  const US_PORT_KEYS = new Set(US_PORTS.map((p) => p.key));
  const ports = (landTable?.ports || US_PORTS.map((p) => ({ key: p.key, label: p.label })))
    .filter((p) => US_PORT_KEYS.has(p.key));
  const seaPorts = landTable?.seaPorts || EU_PORTS.map((p) => ({ key: p.key, label: p.label }));
  const euCountries = landTable?.euCountries || [];

  // Tab-1 port chooser modal — pops up when the chosen origin doesn't ship to
  // the chosen port, letting the user pick an available port (no silent swap).
  const [portModal, setPortModal] = useState(null);
  // Remember the last port that had a valid route, to restore it if the user
  // dismisses the modal without choosing (so an invalid port never sticks).
  const lastValidPortRef = useRef("");
  useEffect(() => {
    if (landPrice > 0 && portKey) lastValidPortRef.current = portKey;
  }, [landPrice, portKey]);

  const openPortChooser = () => {
    const o = landTable?.origins?.find((x) => x.name === originKey);
    if (!o?.prices) return;
    const avail = ports
      .filter((p) => o.prices[p.key] != null)
      .map((p) => ({ key: p.key, label: p.label, price: o.prices[p.key] }));
    if (avail.length) setPortModal({ origin: originKey, ports: avail });
  };

  // Dismiss without choosing → revert to the last valid port for this origin,
  // or clear the (invalid) port so no wrong route is kept.
  const dismissPortModal = () => {
    setPortModal(null);
    const o = landTable?.origins?.find((x) => x.name === originKey);
    const lv = lastValidPortRef.current;
    if (lv && o?.prices?.[lv] != null) onPortChange?.(lv);
    else onPortChange?.("");
  };

  // Track what changed so we react differently:
  //  • origin changed → old port no longer valid → clear it (user re-picks in
  //    the TO dropdown, which resets to "— select port —");
  //  • port changed to an invalid one (same origin) → pop the chooser modal.
  const prevSelRef = useRef({ origin: originKey, port: portKey });
  useEffect(() => {
    const prev = prevSelRef.current;
    prevSelRef.current = { origin: originKey, port: portKey };
    if (tab !== "port" || !landTable || !originKey || !portKey) { setPortModal(null); return; }
    const o = landTable.origins?.find((x) => x.name === originKey);
    if (!o) return;
    if (o.prices?.[portKey] != null) { setPortModal(null); return; } // valid pair → nothing
    if (prev.origin !== originKey) {
      onPortChange?.(""); // location changed → reset port, choose via dropdown
      setPortModal(null);
    } else {
      openPortChooser(); // picked an invalid port → guide with the modal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, landTable, originKey, portKey]);

  // Tab 2: zone-driven state pick. Reset when leaving the tab.
  const [zoneState, setZoneState] = useState("");

  function openLeadFor(scenario) {
    if (scenario === "auction_to_port") {
      const portLabel = ports.find((p) => p.key === portKey)?.label || portKey;
      setLeadCtx({
        scenario,
        summary: {
          from: originKey || "—",
          to: portLabel || "—",
          route: `${originKey} → ${portLabel}`,
          estimate: landPrice ? `$${formatMoney(landPrice)}` : null,
        },
        payload: {
          vehicleId: bidcars?.id ?? null,
          lotNumber: bidcars?.lotNumber ?? null,
          vin: bidcars?.vin ?? null,
          origin: originKey || null,
          port: portKey || null,
          estimateUsd: landPrice ? { min: landPrice, max: landPrice } : null,
        },
      });
    } else if (scenario === "auction_to_warehouse") {
      const wh = WAREHOUSE_LABEL;
      const zoneEst = ZONE_BY_STATE[zoneState] ? ZONE_PRICES[ZONE_BY_STATE[zoneState]] : null;
      setLeadCtx({
        scenario,
        summary: {
          from: originKey || (zoneState ? STATE_LABEL(zoneState) : "—"),
          to: wh,
          route: `${originKey || zoneState} → ${wh}`,
          estimate: zoneEst ? `$${zoneEst.min}–${zoneEst.max}` : null,
        },
        payload: {
          vehicleId: bidcars?.id ?? null,
          lotNumber: bidcars?.lotNumber ?? null,
          vin: bidcars?.vin ?? null,
          origin: originKey || null,
          originState: zoneState || null,
          estimateUsd: zoneEst ? { min: zoneEst.min, max: zoneEst.max } : null,
        },
      });
    } else if (scenario === "to_europe") {
      const seaLabel = seaPorts.find((p) => p.key === seaPortKey)?.label || seaPortKey;
      const fromLabel = fromMode === "warehouse"
        ? WAREHOUSE_LABEL
        : (originKey || "—");
      const total = (seaPrice || 0) + (euLastMilePrice || 0);
      setLeadCtx({
        scenario,
        summary: {
          from: fromLabel,
          to: seaLabel,
          route: `${fromLabel} → ${seaLabel}${euCountry ? ` → ${euCountry}` : ""}`,
          estimate: total > 0 ? `$${formatMoney(total)}` : null,
        },
        payload: {
          vehicleId: bidcars?.id ?? null,
          lotNumber: bidcars?.lotNumber ?? null,
          vin: bidcars?.vin ?? null,
          fromMode: fromMode || "auction",
          origin: fromMode === "warehouse" ? null : (originKey || null),
          euPort: seaPortKey || null,
          euCountry: euCountry || null,
          estimateUsd: total > 0 ? { min: total, max: total } : null,
        },
      });
    } else {
      return;
    }
    setLeadOpen(true);
  }

  // Determine a "highlight state" for tab 1 from the origin.
  const tab1HighlightState = useMemo(() => {
    if (!originKey) return "";
    const o = landTable?.origins?.find((x) => x.name === originKey);
    // Canadian origins ship via the Montreal port and have no US state — this
    // avoids mis-reading the "CA" (Canada) in names like "Calgary CA" as
    // California. The signal is clean: only Canadian yards price montreal_ca.
    if (o?.prices?.montreal_ca != null) return "";
    return inferStateFromOrigin(originKey) || "";
  }, [originKey, landTable]);

  // Tab 2: zone comes from a clicked state, or is inferred from the origin.
  const baseZoneState = zoneState || tab1HighlightState;
  const baseZone = baseZoneState ? ZONE_BY_STATE[baseZoneState] : null;

  return (
    <div className="logistics-tabs">
      <div className="logistics-tabs__head" role="tablist" aria-label="Delivery scenarios">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "port"}
          className={`logistics-tabs__tab${tab === "port" ? " is-active" : ""}`}
          onClick={() => setTab("port")}
        >
          <span className="logistics-tabs__tab-num">1</span>
          <span className="logistics-tabs__tab-text">
            <span className="logistics-tabs__tab-title">Auction → US port</span>
            <span className="logistics-tabs__tab-sub">Land delivery to a US sea port</span>
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "base"}
          className={`logistics-tabs__tab${tab === "base" ? " is-active" : ""}`}
          onClick={() => setTab("base")}
        >
          <span className="logistics-tabs__tab-num">2</span>
          <span className="logistics-tabs__tab-text">
            <span className="logistics-tabs__tab-title">Auction → Our base</span>
            <span className="logistics-tabs__tab-sub">Distance zones · approx price</span>
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "europe"}
          className={`logistics-tabs__tab${tab === "europe" ? " is-active" : ""}`}
          onClick={() => setTab("europe")}
        >
          <span className="logistics-tabs__tab-num">3</span>
          <span className="logistics-tabs__tab-text">
            <span className="logistics-tabs__tab-title">US → Europe</span>
            <span className="logistics-tabs__tab-sub">Sea (RoRo) to a European port</span>
          </span>
        </button>
      </div>

      {/* ── Tab 1: Auction → US port ─────────────────────────── */}
      {tab === "port" && (
        <div className="logistics-tabs__panel" role="tabpanel">
          <div className="logistics-tabs__layout">
            <aside className="ship-card">
              <div className="ship-card__head">
                <span className="ship-card__head-num">1</span>
                <span className="ship-card__head-text">Shipping route</span>
              </div>

              <div className="ship-card__leg">
                <span className="ship-card__leg-dot ship-card__leg-dot--from" />
                <div className="ship-card__leg-body">
                  <span className="ship-card__leg-label">From <em>(auction location)</em></span>
                  <select
                    className="ship-card__select"
                    value={originKey}
                    onChange={(e) => onOriginChange?.(e.target.value)}
                    disabled={!landTable}
                  >
                    <option value="">— select origin —</option>
                    {(landTable?.origins || []).map((o) => (
                      <option key={o.name} value={o.name}>{o.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ship-card__leg">
                <span className="ship-card__leg-dot ship-card__leg-dot--to" />
                <div className="ship-card__leg-body">
                  <span className="ship-card__leg-label">To <em>(US port)</em></span>
                  <select
                    className="ship-card__select"
                    value={portKey}
                    onChange={(e) => onPortChange?.(e.target.value)}
                    disabled={!landTable}
                  >
                    <option value="">— select port —</option>
                    {ports.map((p) => {
                      const o = landTable?.origins?.find((x) => x.name === originKey);
                      const price = o?.prices?.[p.key];
                      return (
                        <option key={p.key} value={p.key}>
                          {p.label}{price != null ? ` — $${price}` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="ship-card__estimate">
                {landPrice > 0 ? (
                  <>
                    <span className="ship-card__estimate-label">Approximate land delivery</span>
                    <span className="ship-card__estimate-value">${formatMoney(landPrice)}</span>
                  </>
                ) : originKey && portKey ? (
                  <div className="ship-card__noroute">
                    <span className="ship-card__estimate-hint">
                      No direct route from <b>{originKey}</b> to this port.
                    </span>
                    <button type="button" className="ship-card__noroute-open" onClick={openPortChooser}>
                      Choose available port
                    </button>
                  </div>
                ) : (
                  <span className="ship-card__estimate-hint">
                    Pick origin and port to see an approximate price.
                  </span>
                )}
              </div>

              <button
                type="button"
                className="ship-card__cta"
                disabled={!originKey || !portKey}
                onClick={() => openLeadFor("auction_to_port")}
              >
                Get exact quote (manager call)
              </button>
            </aside>

            <div className="logistics-tabs__map-wrap">
              <USStatesMap
                mode="select"
                selectedState={tab1HighlightState}
                highlightPort={portKey}
                routeActive={landPrice > 0}
                onSelectPort={(k) => onPortChange?.(k)}
                showPorts
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 2: Auction → Our base ───────────────────────── */}
      {tab === "base" && (
        <div className="logistics-tabs__panel" role="tabpanel">
          <div className="logistics-tabs__layout logistics-tabs__layout--zones">
            <div className="logistics-tabs__map-wrap">
              <USStatesMap
                mode="zones"
                selectedState={baseZoneState}
                onSelectState={(code) => setZoneState(code)}
                showPorts={false}
                hideInlineLegend
              />
            </div>

            <div className="logistics-tabs__zonebar">
              <span className="logistics-tabs__zonebar-title">Distance to {WAREHOUSE_LABEL} · approx land cost</span>
              <div className="logistics-tabs__zonebar-scale">
                {Object.entries(ZONE_PRICES).map(([k, v]) => (
                  <div
                    key={k}
                    className={`logistics-tabs__zoneseg logistics-tabs__zoneseg--${k}${baseZone === k ? " is-active" : ""}`}
                  >
                    <span className="logistics-tabs__zoneseg-bar" />
                    <span className="logistics-tabs__zoneseg-range">{v.label}</span>
                    <span className="logistics-tabs__zoneseg-price">${v.min}–${v.max}</span>
                  </div>
                ))}
              </div>
            </div>

            <aside className="ship-card">
              <div className="ship-card__head">
                <span className="ship-card__head-num">2</span>
                <span className="ship-card__head-text">Shipping route</span>
              </div>

              <div className="ship-card__leg">
                <span className="ship-card__leg-dot ship-card__leg-dot--from" />
                <div className="ship-card__leg-body">
                  <span className="ship-card__leg-label">From <em>(auction location)</em></span>
                  <select
                    className="ship-card__select"
                    value={originKey}
                    onChange={(e) => onOriginChange?.(e.target.value)}
                    disabled={!landTable}
                  >
                    <option value="">— select origin —</option>
                    {(landTable?.origins || []).map((o) => (
                      <option key={o.name} value={o.name}>{o.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {baseZone ? (
                <div className="ship-card__estimate">
                  <span className="ship-card__estimate-label">{STATE_LABEL(baseZoneState)} → our base</span>
                  <span className="ship-card__estimate-value">
                    ${ZONE_PRICES[baseZone].min}–${ZONE_PRICES[baseZone].max}
                  </span>
                </div>
              ) : (
                <div className="ship-card__estimate ship-card__estimate--plain">
                  <span className="ship-card__estimate-hint">
                    Pick your auction location, or click your state on the map, to see the range.
                  </span>
                </div>
              )}

              <button
                type="button"
                className="ship-card__cta"
                disabled={!baseZoneState && !originKey}
                onClick={() => openLeadFor("auction_to_warehouse")}
              >
                Get exact quote (manager call)
              </button>
            </aside>
          </div>
        </div>
      )}

      {/* ── Tab 3: US → Europe ──────────────────────────────── */}
      {tab === "europe" && (
        <div className="logistics-tabs__panel" role="tabpanel">
          <div className="logistics-tabs__layout">
            <aside className="ship-card">
              <div className="ship-card__head">
                <span className="ship-card__head-num">3</span>
                <span className="ship-card__head-text">Shipping route</span>
              </div>

              <div className="ship-card__leg">
                <span className="ship-card__leg-dot ship-card__leg-dot--from" />
                <div className="ship-card__leg-body">
                  <span className="ship-card__leg-label">From <em>(departure)</em></span>
                  <select
                    className="ship-card__select"
                    value={fromMode === "warehouse" ? "warehouse" : portKey}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "warehouse") {
                        onFromModeChange?.("warehouse");
                        // Base ships out of the nearest east-coast port for sea rates.
                        onPortChange?.("newark_nj");
                      } else {
                        onFromModeChange?.("auction");
                        onPortChange?.(v);
                      }
                      onSeaPortChange?.("");
                      onEuCountryChange?.("");
                    }}
                    disabled={!landTable}
                  >
                    <option value="">— select departure —</option>
                    <optgroup label="US sea ports">
                      {ports.map((p) => (
                        <option key={p.key} value={p.key}>{p.label}</option>
                      ))}
                    </optgroup>
                    <option value="warehouse">Our base — {WAREHOUSE_LABEL}</option>
                  </select>
                </div>
              </div>

              <div className="ship-card__leg">
                <span className="ship-card__leg-dot ship-card__leg-dot--to" />
                <div className="ship-card__leg-body">
                  <span className="ship-card__leg-label">To <em>(EU arrival port)</em></span>
                  <select
                    className="ship-card__select"
                    value={seaPortKey}
                    onChange={(e) => {
                      onSeaPortChange?.(e.target.value);
                      onEuCountryChange?.("");
                    }}
                    disabled={!fromMode || !portKey}
                  >
                    <option value="">— select EU port —</option>
                    {seaPorts.map((sp) => {
                      const price = landTable?.seaRates?.[portKey]?.[sp.key];
                      return (
                        <option key={sp.key} value={sp.key}>
                          {sp.label}{price != null ? ` — $${price}` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {!portKey && (
                <div className="ship-card__hint">
                  <span className="ship-card__hint-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                  </span>
                  Pick a departure point above — sea freight rates depend on the US port of loading.
                </div>
              )}

              <div className="ship-card__estimate">
                {seaPrice > 0 ? (
                  <>
                    <span className="ship-card__estimate-label">Approximate sea + last-mile</span>
                    <span className="ship-card__estimate-value">
                      ${formatMoney(seaPrice + (euLastMilePrice || 0))}
                    </span>
                  </>
                ) : (
                  <span className="ship-card__estimate-hint">
                    Pick source and EU port to see an approximate price.
                  </span>
                )}
              </div>

              <button
                type="button"
                className="ship-card__cta"
                disabled={!fromMode || !seaPortKey}
                onClick={() => openLeadFor("to_europe")}
              >
                Get exact quote (manager call)
              </button>
            </aside>

            <div className="logistics-tabs__map-wrap">
              <EuPortsMap
                selectedPort={seaPortKey}
                onSelectPort={(k) => {
                  onSeaPortChange?.(k);
                  onEuCountryChange?.("");
                }}
              />
            </div>
          </div>

          {seaPortKey && euCountries.length > 0 && (
            <div className="logistics-tabs__legend-bar">
              <span className="logistics-tabs__legend-title">Last-mile EU country (optional)</span>
              <div className="logistics-tabs__chips">
                <button
                  type="button"
                  className={`logistics-tabs__chip${!euCountry ? " is-active" : ""}`}
                  onClick={() => onEuCountryChange?.("")}
                >
                  <span className="logistics-tabs__chip-label">Port only</span>
                </button>
                {euCountries
                  .filter((c) => landTable?.euLastMile?.[seaPortKey]?.[c.code] != null)
                  .map((c) => {
                    const price = landTable?.euLastMile?.[seaPortKey]?.[c.code];
                    return (
                      <button
                        key={c.code}
                        type="button"
                        className={`logistics-tabs__chip${euCountry === c.code ? " is-active" : ""}`}
                        onClick={() => onEuCountryChange?.(c.code)}
                      >
                        <span className="logistics-tabs__chip-label">{c.label}</span>
                        {price != null && (
                          <span className="logistics-tabs__chip-price">+${price}</span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      <LeadModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        scenario={leadCtx?.scenario}
        summary={leadCtx?.summary}
        payload={leadCtx?.payload}
      />

      {portModal && (
        <div className="port-modal__overlay" onClick={dismissPortModal}>
          <div className="port-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="port-modal__close" onClick={dismissPortModal} aria-label="Close">×</button>
            <div className="port-modal__title">Choose arrival port</div>
            <div className="port-modal__sub">
              <b>{portModal.origin}</b> ships to these US ports:
            </div>
            <div className="port-modal__list">
              {portModal.ports.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`port-modal__port${p.key === portKey ? " is-active" : ""}`}
                  onClick={() => { onPortChange?.(p.key); setPortModal(null); }}
                >
                  <span className="port-modal__port-name"><span aria-hidden="true">⚓</span> {p.label}</span>
                  <span className="port-modal__port-price">${p.price}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatMoney(n) {
  if (!Number.isFinite(Number(n))) return "0";
  return Math.round(Number(n))
    .toString()
    .replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, "$1,");
}

function STATE_LABEL(code) {
  return STATE_NAMES[code] || code;
}

const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

// Best-effort origin → US state code. Returns "" for Canadian yards (province
// code, "CANADA", or a trailing "CA" country tag) so "Calgary CA" / "…/QC CA"
// aren't mis-read as California.
const CA_PROVINCES = ["QC", "ON", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE", "NT", "YT", "NU"];
function inferStateFromOrigin(name) {
  if (!name) return "";
  const upper = String(name).toUpperCase();
  if (/\bCANADA\b/.test(upper)) return "";
  if (CA_PROVINCES.some((p) => new RegExp(`[\\s\\-,/(]${p}(?:[\\s)/]|$)`).test(upper))) return "";
  const m = upper.match(/[\s\-,]([A-Z]{2})(?:\s|$)/);
  if (m && STATE_NAMES[m[1]]) return m[1];
  return "";
}
